import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, AlertTriangle, GraduationCap, BookOpen, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { api } from '../lib/api';
import { useLanguage } from '../i18n';

const MAX_POLLS = 12;

export default function PaymentResult({ kind }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const sessionId = searchParams.get('session_id');
  const [phase, setPhase] = useState(kind === 'cancel' ? 'cancelled' : 'verifying'); // verifying | success | error | cancelled
  const [result, setResult] = useState(null);
  const pollCount = useRef(0);

  useEffect(() => {
    if (kind === 'cancel') return undefined;
    if (!sessionId) {
      setPhase('error');
      return undefined;
    }
    let timer;
    const poll = async () => {
      pollCount.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setResult(data);
          setPhase('success');
          return;
        }
        if (['failed', 'expired'].includes(data.payment_status)) {
          setPhase('error');
          return;
        }
      } catch (err) {
        console.warn('Payment status poll failed (transient, will retry):', err);
      }
      if (pollCount.current >= MAX_POLLS) {
        setPhase('error');
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [kind, sessionId]);

  return (
    <div className="max-w-md mx-auto pt-6" data-testid="payment-result-page">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="ss-card p-8 text-center space-y-5">
        {phase === 'verifying' && (
          <div className="space-y-4" data-testid="payment-verifying">
            <span className="inline-flex w-16 h-16 rounded-[20px] bg-white/[0.07] border border-white/12 items-center justify-center">
              <Loader2 size={28} className="animate-spin text-white/70" />
            </span>
            <h1 className="font-display font-bold text-xl text-white">{t('paymentProcessing')}</h1>
          </div>
        )}

        {phase === 'success' && (
          <div className="space-y-4" data-testid="payment-success">
            <span className="inline-flex w-16 h-16 rounded-[20px] bg-[rgba(34,197,94,0.12)] border border-[rgba(34,197,94,0.35)] items-center justify-center">
              <CheckCircle2 size={30} className="text-[#22C55E]" />
            </span>
            <h1 className="font-display font-bold text-xl text-white">{t('paymentSuccess')}</h1>
            {result?.product_type === 'course' ? (
              <button
                onClick={() => navigate(`/course/${result.product_id}`)}
                data-testid="payment-go-course"
                className="ss-btn-primary w-full h-12 text-sm font-bold font-display flex items-center justify-center gap-2"
              >
                <BookOpen size={16} /> {t('goToCourse')}
              </button>
            ) : (
              <button
                onClick={() => navigate('/academy?tab=reviews')}
                data-testid="payment-go-reviews"
                className="ss-btn-primary w-full h-12 text-sm font-bold font-display flex items-center justify-center gap-2"
              >
                <GraduationCap size={16} /> {t('goToReviews')}
              </button>
            )}
          </div>
        )}

        {phase === 'cancelled' && (
          <div className="space-y-4" data-testid="payment-cancelled">
            <span className="inline-flex w-16 h-16 rounded-[20px] bg-[rgba(233,196,106,0.1)] border border-[rgba(233,196,106,0.3)] items-center justify-center">
              <XCircle size={30} className="text-[#E9C46A]" />
            </span>
            <h1 className="font-display font-bold text-xl text-white">{t('paymentCancelled')}</h1>
            <p className="text-sm text-white/55">{t('paymentCancelledSub')}</p>
          </div>
        )}

        {phase === 'error' && (
          <div className="space-y-4" data-testid="payment-error">
            <span className="inline-flex w-16 h-16 rounded-[20px] bg-[rgba(231,111,123,0.1)] border border-[rgba(231,111,123,0.3)] items-center justify-center">
              <AlertTriangle size={28} className="text-[#E76F7B]" />
            </span>
            <h1 className="font-display font-bold text-xl text-white">{t('paymentError')}</h1>
          </div>
        )}

        {phase !== 'verifying' && (
          <button onClick={() => navigate('/academy')} data-testid="payment-back-academy" className="ss-btn-glass w-full h-11 text-sm font-medium flex items-center justify-center gap-2">
            <ArrowLeft size={15} /> {t('backToAcademy')}
          </button>
        )}
      </motion.div>
    </div>
  );
}
